import { useState, useCallback, useEffect, useRef } from 'react';
import { Announcement, AnnouncementType, ANNOUNCEMENT_DURATIONS } from '../config/announcements';

export interface UseAnnouncementsReturn {
  currentAnnouncement: Announcement | null;
  showAnnouncement: (type: AnnouncementType, lines: string[], onDismiss?: () => void) => void;
  dismissAnnouncement: () => void;
}

/**
 * Hook pour gérer l'affichage des annonces arbitre
 *
 * - Une seule annonce visible à la fois
 * - Une nouvelle annonce remplace automatiquement l'ancienne
 * - L'annonce reste affichée jusqu'au clic de l'utilisateur ou auto-dismiss
 * - Supporte un callback onDismiss pour exécuter une action après fermeture
 */
export function useAnnouncements(): UseAnnouncementsReturn {
  const [currentAnnouncement, setCurrentAnnouncement] = useState<Announcement | null>(null);
  const onDismissCallback = useRef<(() => void) | null>(null);

  // Auto-dismiss après la durée configurée pour chaque type d'annonce
  useEffect(() => {
    if (!currentAnnouncement) return;

    const duration = ANNOUNCEMENT_DURATIONS[currentAnnouncement.type] * 1000;
    const timer = setTimeout(() => {
      // Appeler le callback avant de fermer
      if (onDismissCallback.current) {
        onDismissCallback.current();
        onDismissCallback.current = null;
      }
      setCurrentAnnouncement(null);
    }, duration);

    // Cleanup: annuler le timer si l'annonce change ou est fermée manuellement
    return () => clearTimeout(timer);
  }, [currentAnnouncement]);

  const showAnnouncement = useCallback((type: AnnouncementType, lines: string[], onDismiss?: () => void) => {
    // Note: On ne déclenche PAS le callback de l'annonce précédente quand elle est remplacée
    // Le callback ne doit être appelé que quand l'utilisateur ferme l'annonce ou après auto-dismiss
    onDismissCallback.current = onDismiss || null;
    setCurrentAnnouncement({
      type,
      lines,
      timestamp: Date.now(),
    });
  }, []);

  const dismissAnnouncement = useCallback(() => {
    // Appeler le callback avant de fermer
    if (onDismissCallback.current) {
      onDismissCallback.current();
      onDismissCallback.current = null;
    }
    setCurrentAnnouncement(null);
  }, []);

  return {
    currentAnnouncement,
    showAnnouncement,
    dismissAnnouncement,
  };
}
